import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { seedAccommodations, typeLabels, typeColors, typeImages, Accommodation } from "@/mocks/accommodations";

const FEATURED_BADGES: Record<string, { label: string; color: string }> = {
  "kapadokya-cave-suites": { label: "Editör Seçimi", color: "bg-amber-500 text-white" },
  "oludeniz-blue-bay-hotel": { label: "En Popüler", color: "bg-rose-500 text-white" },
  "safranbolu-konak-otel": { label: "UNESCO Mirası", color: "bg-emerald-600 text-white" },
  "mardin-tas-ev-pansiyon": { label: "Gizli Hazine", color: "bg-stone-800 text-white" },
  "ayder-doga-glamping": { label: "Doğa Kaçamağı", color: "bg-lime-600 text-white" },
};

const FEATURED_RATINGS: Record<string, number> = {
  "kapadokya-cave-suites": 4.9,
  "mardin-tas-ev-pansiyon": 4.7,
  "oludeniz-blue-bay-hotel": 4.8,
  "safranbolu-konak-otel": 4.6,
  "ayder-doga-glamping": 4.8,
  "sapanca-orman-bungalovlari": 4.5,
};

const FeaturedSlider = () => {
  const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const approved = (() => {
      try {
        return JSON.parse(localStorage.getItem("pendingAccommodations") || "[]")
          .filter((a: Accommodation) => a.status === "approved");
      } catch { return []; }
    })();
    const all = [...seedAccommodations, ...approved];
    // Sort by rating desc, take top 5
    const sorted = all
      .map((a) => ({ ...a, _rating: FEATURED_RATINGS[a.id] || 4.5 }))
      .sort((a, b) => b._rating - a._rating)
      .slice(0, 5);
    setAccommodations(sorted);
  }, []);

  useEffect(() => {
    if (accommodations.length < 2) return;
    intervalRef.current = setInterval(() => {
      goNext();
    }, 5000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [accommodations.length, activeIdx]);

  const goTo = (idx: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveIdx(idx);
    setTimeout(() => setIsAnimating(false), 400);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const goNext = () => {
    goTo((activeIdx + 1) % accommodations.length);
  };

  const goPrev = () => {
    goTo((activeIdx - 1 + accommodations.length) % accommodations.length);
  };

  if (accommodations.length === 0) return null;

  const active = accommodations[activeIdx];
  const imgs = typeImages[active.businessType] || typeImages.hotel;
  const badge = FEATURED_BADGES[active.id];
  const rating = FEATURED_RATINGS[active.id] || 4.5;

  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Öne Çıkan İşletmeler
          </h2>
          <p className="text-stone-400 text-sm mt-1">Editörlerimizin önerdiği en iyi konaklama seçenekleri</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            className="w-9 h-9 flex items-center justify-center border border-stone-200 rounded-full text-stone-600 hover:bg-stone-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            onClick={goNext}
            className="w-9 h-9 flex items-center justify-center border border-stone-200 rounded-full text-stone-600 hover:bg-stone-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>

      <div className="relative rounded-2xl overflow-hidden bg-stone-900" style={{ height: "420px" }}>
        {/* Background image */}
        <div
          key={active.id}
          className="absolute inset-0 transition-opacity duration-500"
          style={{ opacity: isAnimating ? 0 : 1 }}
        >
          <img
            src={imgs[0]}
            alt={active.businessName}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        </div>

        {/* Content */}
        <div
          className="absolute inset-0 flex items-end p-8 md:p-12 transition-opacity duration-500"
          style={{ opacity: isAnimating ? 0 : 1 }}
        >
          <div className="max-w-lg">
            <div className="flex flex-wrap gap-2 mb-3">
              {badge && (
                <span className={`text-xs px-3 py-1 rounded-full font-semibold ${badge.color}`}>
                  {badge.label}
                </span>
              )}
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${typeColors[active.businessType] || "bg-stone-100 text-stone-600"}`}>
                {typeLabels[active.businessType] || active.businessType}
              </span>
            </div>

            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {active.businessName}
            </h3>

            <div className="flex flex-wrap items-center gap-4 mb-3 text-white/80 text-sm">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-amber-400 text-sm"></i>
                </div>
                <span>{active.city} · {active.district}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-amber-400 text-sm"></i>
                </div>
                <span className="font-semibold text-amber-300">{rating.toFixed(1)}</span>
                <span className="text-white/50 text-xs">/ 5.0</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-money-cny-circle-line text-amber-400 text-sm"></i>
                </div>
                <span>{active.priceRange.split(" ")[0]}</span>
              </div>
            </div>

            <p className="text-white/70 text-sm leading-relaxed mb-5 line-clamp-2">
              {active.shortDesc}
            </p>

            <div className="flex gap-3">
              <Link
                to={`/accommodation/${active.id}`}
                className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-eye-line"></i> Detayları Gör
              </Link>
              <Link
                to="/accommodations"
                className="flex items-center gap-2 px-5 py-2.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/30 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Tümünü Gör
              </Link>
            </div>
          </div>
        </div>

        {/* Thumbnail strip — right side */}
        <div className="absolute right-6 top-1/2 -translate-y-1/2 flex flex-col gap-2">
          {accommodations.map((acc, idx) => {
            const thumbImgs = typeImages[acc.businessType] || typeImages.hotel;
            return (
              <button
                key={acc.id}
                onClick={() => goTo(idx)}
                className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${
                  idx === activeIdx ? "border-amber-400 opacity-100 scale-110" : "border-white/20 opacity-50 hover:opacity-80"
                }`}
              >
                <img src={thumbImgs[0]} alt={acc.businessName} className="w-full h-full object-cover object-top" />
              </button>
            );
          })}
        </div>

        {/* Dot indicators */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
          {accommodations.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx)}
              className={`rounded-full transition-all cursor-pointer ${
                idx === activeIdx ? "w-6 h-2 bg-amber-400" : "w-2 h-2 bg-white/40 hover:bg-white/60"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedSlider;
